import { Link } from 'react-router-dom';
import { ArrowUpRight, Box } from 'lucide-react';
import { resolveMediaUrl } from '../utils/media';

export default function ProductCard({ product, size = 'normal' }) {
  const stockStatusMap = {
    in_stock: { label: 'In Stock', color: 'text-emerald-400' },
    made_to_order: { label: 'Made to Order', color: 'text-amber-400' },
    sold_out: { label: 'Sold Out', color: 'text-red-400' },
  };

  const status = stockStatusMap[product.stockStatus] || stockStatusMap.in_stock;

  return (
    <Link
      to={`/product/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className={`group block bg-[#121212] border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/10 ${
        size === 'large' ? 'md:col-span-2 md:row-span-2' : ''
      }`}
    >
      {/* Image Container */}
      <div className={`relative overflow-hidden bg-gradient-to-br from-zinc-900 to-black ${
        size === 'large' ? 'aspect-square md:aspect-[4/3]' : 'aspect-square'
      }`}>
        <img
          src={resolveMediaUrl(product.thumbnailImage)}
          alt={product.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* 3D Badge */}
        {product.glbModelUrl && (
          <div className="absolute top-4 left-4 px-3 py-1 bg-indigo-500/90 backdrop-blur-sm rounded-full flex items-center space-x-1">
            <Box className="w-3 h-3 text-white" />
            <span className="text-xs font-medium text-white">3D View</span>
          </div>
        )}
        
        {/* Sale Badge */}
        {product.hasDiscount && (
          <div className="absolute top-4 right-4 px-3 py-1 bg-red-500/90 backdrop-blur-sm rounded-full">
            <span className="text-xs font-medium text-white">-{product.discountPercent}%</span>
          </div>
        )}
        
        {/* Featured Badge (only if no discount) */}
        {product.featured && !product.hasDiscount && (
          <div className="absolute top-4 right-4 px-3 py-1 bg-amber-500/90 backdrop-blur-sm rounded-full">
            <span className="text-xs font-medium text-black">Featured</span>
          </div>
        )}
        
        {/* Quick View Button */}
        <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
          <div className="p-3 bg-indigo-500 rounded-full">
            <ArrowUpRight className="w-5 h-5 text-white" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-indigo-400 mb-1 block">
              {product.category}
            </span>
            <h3 className={`font-['Outfit'] font-medium text-white group-hover:text-indigo-400 transition-colors truncate ${
              size === 'large' ? 'text-xl md:text-2xl' : 'text-lg'
            }`}>
              {product.title}
            </h3>
            <p className="text-zinc-500 text-sm mt-1 line-clamp-2">
              {product.shortDescription}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
          <div className="flex items-baseline gap-2">
            <span className={`font-['Outfit'] font-semibold text-white ${
              size === 'large' ? 'text-2xl' : 'text-xl'
            }`}>
              €{product.priceEUR.toFixed(2)}
            </span>
            {product.hasDiscount && product.originalPrice && (
              <span className="text-sm text-zinc-500 line-through">
                €{product.originalPrice.toFixed(2)}
              </span>
            )}
          </div>
          <span className={`text-xs font-medium ${status.color}`}>
            {status.label}
          </span>
        </div>
      </div>
    </Link>
  );
}
